import type { DeviceClientHints } from '@troia-v3/types';

/**
 * User-Agent Client Hints de alta entropia.
 *
 * Necessário porque o Chrome **removeu** do User-Agent os dados que a tela de
 * dispositivos precisa: desde a v90 o macOS reporta sempre `10_15_7`, e desde a
 * v110 (2023) o Android reporta modelo `"K"` e versão `"10"` fixos. Nenhuma
 * biblioteca de parse recupera isso — só esta API.
 *
 * É nativa (sem dependência) e só existe em browsers Chromium. Em Safari e
 * Firefox devolve `undefined`, e o backend segue apenas com o User-Agent.
 */

interface UADataValues {
  model?: string;
  platform?: string;
  platformVersion?: string;
  fullVersionList?: Array<{ brand: string; version: string }>;
}

interface NavigatorUAData {
  getHighEntropyValues?: (hints: string[]) => Promise<UADataValues>;
}

/** Marcas genéricas do `fullVersionList` — a do navegador de verdade é a outra. */
const GENERIC_BRANDS = ['Not', 'Chromium', 'Brand'];

export async function collectClientHints(): Promise<DeviceClientHints | undefined> {
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;
  if (!uaData?.getHighEntropyValues) return undefined;

  try {
    const values = await uaData.getHighEntropyValues(['model', 'platformVersion', 'fullVersionList']);
    const hints: DeviceClientHints = {};

    if (values.model) hints.model = values.model;
    if (values.platform) hints.platform = values.platform;
    if (values.platformVersion) hints.platformVersion = values.platformVersion;

    const realBrand = values.fullVersionList?.find(
      (entry) => !GENERIC_BRANDS.some((generic) => entry.brand.includes(generic)),
    );
    if (realBrand) hints.browserVersion = realBrand.version;

    return Object.keys(hints).length > 0 ? hints : undefined;
  } catch {
    return undefined;
  }
}
